import { A2vAssetType, A2vUploadStatus } from '../../model';

export interface AssetUploadQueueItem {
  id: string;
  file: File;
  assetType: A2vAssetType;
  width: number | null;
  height: number | null;
  status: A2vUploadStatus;
  error?: string | undefined;
}

const supportedExtensions = new Set(['jpg', 'jpeg', 'svg', 'png', 'mp4']);

export function assetFileId(file: File) {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}

export function isSupportedAssetFile(file: File) {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return supportedExtensions.has(extension);
}

export function assetTypeForFile(file: File) {
  return file.name.toLowerCase().endsWith('.mp4') || file.type.startsWith('video/')
    ? A2vAssetType.Video
    : A2vAssetType.Image;
}

export async function createAssetUploadQueueItem(file: File): Promise<AssetUploadQueueItem> {
  const assetType = assetTypeForFile(file);
  const dimensions = await readAssetDimensions(file, assetType);
  return {
    id: assetFileId(file),
    file,
    assetType,
    width: dimensions.width,
    height: dimensions.height,
    status: A2vUploadStatus.Pending,
  };
}

export function hasValidAssetDimensions(
  item: AssetUploadQueueItem,
): item is AssetUploadQueueItem & { width: number; height: number } {
  return validDimension(item.width) && validDimension(item.height);
}

function readAssetDimensions(file: File, assetType: A2vAssetType) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    // Revoked by `cleanup` on every load and error path below.
    // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };
    const resolveDimensions = (width: number, height: number) => {
      cleanup();
      if (!validDimension(width) || !validDimension(height)) {
        reject(new Error('Asset dimensions must be finite positive numbers.'));
        return;
      }
      resolve({ width, height });
    };

    if (assetType === A2vAssetType.Video) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolveDimensions(video.videoWidth, video.videoHeight);
      };
      video.onerror = () => {
        cleanup();
        reject(new Error('Unable to read video metadata.'));
      };
      video.src = objectUrl;
      return;
    }

    const image = new Image();
    image.onload = () => {
      resolveDimensions(image.naturalWidth, image.naturalHeight);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('Unable to read image metadata.'));
    };
    image.src = objectUrl;
  });
}

function validDimension(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}
